/**
 * Founder Control Center · Sección 8 · Automation · Sprint T-16.1.
 *
 * Single source of truth para Automation Center. Consume los 14 endpoints
 * del Automation Engine T-16 vía React Query · 1 endpoint = 1 widget.
 *
 * 10 sub-tabs (orden fijado por founder · §3.4):
 *   1. Overview   → OverviewTab      (/info + /metrics + /executions running + /dlq + /events)
 *   2. Jobs       → JobsTab          (/jobs + run/pause/resume)
 *   3. Scheduler  → SchedulerTab     (/jobs · vista declarativa · sin next_run_at)
 *   4. Queue      → QueueTab         (/executions?status=pending|retry · poll 10s)
 *   5. Running    → RunningTab       (/executions?status=running · poll 5s)
 *   6. History    → HistoryTab       (/executions filtrado + paginado)
 *   7. DLQ        → DlqTab           (/dlq + retry + discard)
 *   8. Intents    → IntentsTab       (/notifications/intents · read-only T-16)
 *   9. Metrics    → MetricsTab       (/metrics · poll 30s)
 *   10. Events    → EventsTab        (/events · manual + toggle 15s)
 *
 * Filosofía T-16.1:
 *   - 0 lógica de negocio en React
 *   - 1 endpoint = 1 widget
 *   - 0 librerías nuevas
 *   - 0 modificaciones al backend (T-16 permanece intacto)
 *   - AI Platform Freeze 100%
 */
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  BarChart3, Cog, Calendar, ListChecks, Play, Clock,
  AlertOctagon, Mail, Gauge, Activity,
} from "lucide-react";
import SubTabs, { useSubTab } from "./SubTabs";
import OverviewTab from "../../../components/automationCenter/OverviewTab";
import JobsTab from "../../../components/automationCenter/JobsTab";
import SchedulerTab from "../../../components/automationCenter/SchedulerTab";
import QueueTab from "../../../components/automationCenter/QueueTab";
import RunningTab from "../../../components/automationCenter/RunningTab";
import HistoryTab from "../../../components/automationCenter/HistoryTab";
import DlqTab from "../../../components/automationCenter/DlqTab";
import IntentsTab from "../../../components/automationCenter/IntentsTab";
import MetricsTab from "../../../components/automationCenter/MetricsTab";
import EventsTab from "../../../components/automationCenter/EventsTab";

const ITEMS = [
  { key: "overview",  label: "Overview",   icon: BarChart3 },
  { key: "jobs",      label: "Jobs",       icon: Cog },
  { key: "scheduler", label: "Scheduler",  icon: Calendar },
  { key: "queue",     label: "Queue",      icon: ListChecks },
  { key: "running",   label: "Running",    icon: Play },
  { key: "history",   label: "History",    icon: Clock },
  { key: "dlq",       label: "DLQ",        icon: AlertOctagon },
  { key: "intents",   label: "Intents",    icon: Mail },
  { key: "metrics",   label: "Metrics",    icon: Gauge },
  { key: "events",    label: "Events",     icon: Activity },
];

export default function AutomationSection() {
  const [sub, setSub] = useSubTab("overview");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Compat: sub key desconocido → overview limpio
  useEffect(() => {
    const current = searchParams.get("sub");
    if (current && !ITEMS.find((i) => i.key === current)) {
      navigate("/founder/automation?sub=overview", { replace: true });
    }
  }, [searchParams, navigate]);

  const goToTab = (key) => setSub(key);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8 space-y-5" data-testid="fcc-automation-section">
      <Header />
      <SubTabs items={ITEMS} value={sub} onChange={setSub} testid="automation-subtabs" />
      <div className="pt-2">
        {sub === "overview"  && <OverviewTab onNavigate={goToTab} />}
        {sub === "jobs"      && <JobsTab />}
        {sub === "scheduler" && <SchedulerTab />}
        {sub === "queue"     && <QueueTab />}
        {sub === "running"   && <RunningTab />}
        {sub === "history"   && <HistoryTab />}
        {sub === "dlq"       && <DlqTab />}
        {sub === "intents"   && <IntentsTab />}
        {sub === "metrics"   && <MetricsTab />}
        {sub === "events"    && <EventsTab />}
      </div>
    </div>
  );
}

function Header() {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--ag-gold)] font-semibold flex items-center gap-1.5">
        <Activity size={11} /> Founder Control Center · Automation
      </div>
      <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tighter mt-2">
        Automation Center
      </h1>
      <p className="text-xs text-[var(--ag-text-dim)] mt-1">
        Overview · Jobs · Scheduler · Queue · Running · History · DLQ · Intents · Metrics · Events
      </p>
    </div>
  );
}
